import { useEffect, useRef, useState } from 'react';

import { ChevronDown, X } from 'lucide-react';
import Multiselect from 'multiselect-react-dropdown';

import { colors, fieldTypes } from '../../../../data/constants';
import { Loader } from '../../../ui/Loader';
import { InputButtons } from '../InputButtons';
import { SelectInput } from '../SelectInput';

export const InputSelect = ({
  formProps = { values: [] },
  label,
  isGrouped = false,
  isMultiSelect = true,
  isSearchableSelect = true,
  name,
  defaultValue,
  isDataLoading = false,
  fieldType,
  placeholder,
  selectData = undefined,
  buttons = [],
  isResetClicked = false,
  setIsResetClicked,
  setMultiSelectRefs,
  isRequired = false,
  hint = '',
  handleSelection = () => {},
  selectDataRedux = false,
  depedentValue = '',
  hasDepedentValue = false,
}) => {
  const [selectedValue, setSelectedValue] = useState([]);
  const [options, setOptions] = useState(selectData ?? []);
  const [isLabelShown, setIsLabelShown] = useState(false);

  const selectRef = useRef();

  const Label = label;

  const isNewSelect = fieldType === fieldTypes.multiselectGrouped;
  const hasGroup = isGrouped ? 'group' : '';

  const hasDefaultValue =
    defaultValue && defaultValue.length > 0 ? defaultValue : [];
  const isMultiselect =
    fieldType === fieldTypes.multiselect ||
    fieldType === fieldTypes.multiselectDepedent
      ? true
      : false;
  const isSearchableSingle =
    fieldType === fieldTypes.selectSearchable ||
    fieldType === fieldTypes.selectSearchableDependent
      ? true
      : false;

  // --- Handlers for old library path ---

  const handleRemove = (selectedList, removedItem) => {
    const valuesList = selectedList.map(item => item.value);

    if (!removedItem || removedItem.length < 1) {
      setSelectedValue([]);
    } else {
      setSelectedValue(valuesList);
    }

    if (isMultiselect) {
      formProps?.setFieldValue(name, valuesList);
    }

    if (handleSelection && isMultiselect) {
      handleSelection(valuesList, name, removedItem);
    }
  };

  const handleSelect = async (selectedList, selectedItem) => {
    const valuesList = selectedList.map(item => {
      if (Number(item.value)) {
        return Number(item.value);
      } else {
        return item.value;
      }
    });

    setSelectedValue(selectedList);

    if (isMultiselect) {
      await formProps?.setFieldValue(name, valuesList);
    } else if (!isMultiselect) {
      await formProps?.setFieldValue(name, selectedItem.value);
    }

    if (formProps?.errors?.[name]) {
      formProps?.validateField(name);
    }

    if (handleSelection && !isMultiselect) {
      handleSelection(selectedItem, name);
    } else if (handleSelection && isMultiselect) {
      handleSelection(valuesList, name);
    }
  };

  // --- Handlers for new SelectInput path ---

  const toValuesList = items =>
    items.map(item => (Number(item.value) ? Number(item.value) : item.value));

  const handleNewSelect = updatedItems => {
    const valuesList = toValuesList(updatedItems);
    setSelectedValue(updatedItems);
    formProps?.setFieldValue(name, valuesList);
    if (formProps?.errors?.[name]) formProps?.validateField(name);
    handleSelection?.(valuesList, name);
  };

  const handleNewRemove = updatedItems => {
    const valuesList = toValuesList(updatedItems);
    setSelectedValue(updatedItems);
    formProps?.setFieldValue(name, valuesList);
    handleSelection?.(valuesList, name);
  };

  // --- Shared effects ---

  useEffect(() => {
    if (selectDataRedux) {
      setOptions(selectDataRedux);
    }
  }, [selectDataRedux]);

  useEffect(() => {
    if (selectData) {
      setOptions(selectData);
    }
  }, [selectData]);

  useEffect(() => {
    if (setMultiSelectRefs && !isNewSelect) {
      const ref = selectRef;
      setMultiSelectRefs(prevRefs => ({
        ...prevRefs,
        [name]: {
          ...ref,
          setFieldValue: formProps?.setFieldValue,
          setSelectedValue: setSelectedValue,
        },
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !formProps?.values?.[name]?.length &&
      !selectedValue?.length &&
      !hasDefaultValue?.length
    ) {
      setIsLabelShown(false);
    } else {
      setIsLabelShown(true);
    }
  }, [
    formProps?.values?.[name]?.length,
    selectedValue?.length,
    hasDefaultValue?.length,
  ]);

  useEffect(() => {
    if (isResetClicked) {
      setIsLabelShown(false);
      setIsResetClicked(false);
    }
  }, [isResetClicked, setIsResetClicked]);

  // --- Render ---

  return (
    <>
      <div className="flex items-center w-full gap-3 multiselectInput">
        {label && isLabelShown && (
          <Label
            name={name}
            placeholder={placeholder}
            isLabelShown={isLabelShown}
          />
        )}

        {isNewSelect ? (
          <SelectInput
            name={name}
            options={options}
            initialSelected={hasDefaultValue}
            onSelect={handleNewSelect}
            onRemove={handleNewRemove}
            placeholder={placeholder}
            isMulti={isMultiSelect}
            isSearchable={isSearchableSelect}
            isGrouped={isGrouped}
            hasError={!!formProps?.errors?.[name]}
            isDisabled={isDataLoading}
            isResetClicked={isResetClicked}
            setIsResetClicked={setIsResetClicked}
          />
        ) : (
          <Multiselect
            ref={selectRef}
            displayValue="title"
            name={name}
            selectedValues={hasDefaultValue}
            groupBy={hasGroup}
            onKeyPressFn={function noRefCheck() {}}
            onRemove={handleRemove}
            onSearch={function noRefCheck() {}}
            onSelect={handleSelect}
            onFocus={() => {}}
            placeholder={placeholder}
            options={options}
            showCheckbox={isMultiselect && !isSearchableSingle}
            singleSelect={!isMultiselect && !isSearchableSingle}
            selectionLimit={isSearchableSingle ? 1 : undefined}
            hidePlaceholder={true}
            showArrow
            customArrow={
              <ChevronDown
                size={14}
                color={colors.arrowColor}
              />
            }
            customCloseIcon={
              <X
                size={10}
                color={colors.arrowColor}
              />
            }
            style={{
              chips: {
                gap: '4px',
                background: colors.lightGrayColor,
                color: colors.blackColor,
                fontSize: '0.75rem',
                padding: '2px 6px',
              },
              multiSelectContainer: {
                marginTop: 0,
              },
              searchBox: {
                height: '37px',
                minHeight: '37px',
                maxHeight: '37px',
                overflow: 'hidden',
                overflowX: 'auto',
                display: 'flex',
                flexWrap: 'nowrap',
                fontSize: '0.875rem',
                border: 'none',
                borderBottom:
                  formProps.errors[name] &&
                  Object.keys(formProps.errors[name]).length > 0
                    ? '1px solid rgb(232 72 85)'
                    : '1px solid rgb(229 231 235)',
                borderRadius: '0px',
              },
              optionContainer: {
                backgroundColor: '#fff',
              },
            }}
          />
        )}

        {isRequired && (
          <div className="absolute top-[30%] right-[11%] sm:right-[9%]">
            <span className="text-sm text-redColor">*</span>
          </div>
        )}

        {isDataLoading && (
          <div className="absolute top-[36%] right-[16%] sm:right-[13%]">
            <Loader />
          </div>
        )}

        {buttons && buttons.length > 0 && (
          <InputButtons
            buttons={buttons}
            formProps={formProps}
            handleRemove={handleRemove}
            selectedList={selectedValue}
            setSelectedValue={setSelectedValue}
            inputRef={selectRef}
            name={name}
            depedentValue={depedentValue}
            hasDepedentValue={hasDepedentValue}
          />
        )}
      </div>

      {hint && hint.length > 0 && (
        <div>
          <span className="text-[10px] text-gray-600">{hint}</span>
        </div>
      )}
    </>
  );
};
